import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import { CallHistoryMethodAction, push } from 'connected-react-router';

import AppState from '@webapp/store/state';
import { ReleaseType } from '@common/types/release/release-type';

import { Translate } from 'react-localize-redux';

import moment from 'moment';

import { rgba } from 'polished';
import styled from 'styled-components';

import { AnimatePresence } from 'framer-motion';

import Flex from '@components/layout/flex';
import SongIcon from '@components/basics/icons/song';
import Typography from '@components/basics/typography';
import SquareImg from '@components/basics/artwork-img';
import MotionOpacity from '@components/motion/motion-opacity';
import {
  getPreviousReleaseDate,
  getReleaseDate,
} from '@webapp/utils/release/getDate';

interface PublishedPageProps {
  className?: string;
  goToEditRelease: (
    artistId: string,
    releaseId: string
  ) => CallHistoryMethodAction;
  push: (path: string) => CallHistoryMethodAction;
}

interface MappedProps {
  artistId: string;
  releaseId: string;
  releaseCover: string;
  releaseTitle: string;
  releaseType: ReleaseType;
  preOrderDate: string | undefined;
  isMobile: boolean;
  previousReleaseDate?: string;
  releaseDate?: string;
}

const mapStateToProps = (state: AppState): MappedProps => ({
  artistId: state.artist.active?.id || '',
  releaseId: state.release.release?.id || '',
  releaseCover: state.release.release?.cover?.url || '',
  releaseTitle: state.release.release?.title || '',
  releaseType: state.release.release?.type || 'single',
  // releaseDate: state.release.release?.releaseDate,
  preOrderDate: state.release.release?.preorderDate,
  isMobile: state.ui.mobile,
  previousReleaseDate: getPreviousReleaseDate(state.release.release),
  releaseDate: getReleaseDate(state.release.release),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  goToEditRelease: (artistId: string, releaseId: string) =>
    dispatch(push(`/release/${artistId}/${releaseId}/title`)),
  push: (path: string) => dispatch(push(path)),
});

export const CapitalizeText = styled.span`
  text-transform: capitalize;
`;

export const PublishedPage = (props: PublishedPageProps & MappedProps) => {
  return (
    <MotionOpacity className={props.className}>
      <WrapperContent
        flexDirection={props.isMobile ? 'column' : 'row'}
        gap={!props.isMobile ? 30 : 40}
      >
        <AnimatePresence>
          {props.releaseCover ? (
            <MotionOpacity>
              <SquareImg className="artwork" srcImg={props.releaseCover} />
            </MotionOpacity>
          ) : (
            <MotionOpacity>
              <SongIconWrapper>
                <SongIcon dark className="artwork icon" />
              </SongIconWrapper>
            </MotionOpacity>
          )}
        </AnimatePresence>
        <InfoWrapper>
          <div className="mb-40">
            <Title customFont="semi-bold">
              <Translate
                id="RELEASE.PUBLISHED.TITLE"
                data={{
                  releaseName:
                    props.releaseTitle.length > 20
                      ? `${props.releaseTitle.slice(0, 20)}...`
                      : props.releaseTitle,
                }}
              />
            </Title>
            <Text type="small">
              <Translate id="RELEASE.PUBLISHED.TEXT" />
            </Text>
          </div>
          <Flex
            justifyContent="space-between"
            alignItems="center"
            flexDirection={props.isMobile ? 'column' : 'row'}
          >
            <DateWrapper
              justifyContent={props.isMobile ? 'space-between' : 'flex-start'}
              alignItems="center"
              gap={!props.isMobile ? 20 : undefined}
            >
              <DateItem>
                <Typography type="small">
                  <Translate id="RELEASE.PUBLISHED.PRE_RELEASE.DATE" />
                </Typography>
                <Typography weight="700">
                  {moment(props.preOrderDate).format('DD MMM YYYY')}
                </Typography>
              </DateItem>
              <DateItem>
                <Typography type="small">
                  <Translate id="RELEASE.PUBLISHED.RELEASE.DATE" />
                </Typography>
                <Typography weight="700">
                  {moment(props.releaseDate).format('DD MMM YYYY')}
                </Typography>
              </DateItem>
            </DateWrapper>
            <LinkUnderline onClick={() => props.push('/catalog')}>
              <Translate id="RELEASE.PUBLISHED.CONTINUE" />
            </LinkUnderline>
          </Flex>
        </InfoWrapper>
      </WrapperContent>
    </MotionOpacity>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(PublishedPage)`
  @media ${({ theme }) => theme.device.tablet} {
    padding-top: 130px;
  }

  .background {
    position: absolute;
    inset: 0;
    z-index: 0;
  }

  .artwork {
    width: 327px;
    height: 327px;
    margin: 0 auto;
    display: block;
    @media ${({ theme }) => theme.device.tablet} {
      width: 298px;
      height: 298px;
      margin: 0;
    }

    &.icon {
      width: 80%;
      height: 80%;
    }
  }
`);

const WrapperContent = styled(Flex)``;

const Title = styled(Typography)`
  font-size: ${({ theme }) => theme.typography.h2};
  line-height: ${({ theme }) => theme.typography.h2};
  letter-spacing: -0.03em;
  margin-bottom: 20px;

  @media ${({ theme }) => theme.device.tablet} {
    font-size: ${({ theme }) => theme.typography.h1};
    line-height: ${({ theme }) => theme.typography.h1};
    margin-bottom: 40px;
  }
`;

const Text = styled(Typography)`
  color: ${({ theme }) => rgba(theme.colors.primary, 0.5)};
`;

const InfoWrapper = styled.div`
  max-width: calc(100% - 327px - 40px);
  @media ${({ theme }) => theme.device.tablet} {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    max-width: calc(100% - 298px - 30px);
  }
`;

const DateWrapper = styled(Flex)`
  width: 100%;
  margin-bottom: 40px;
  @media ${({ theme }) => theme.device.tablet} {
    width: auto;
    margin: 0;
  }
`;

const DateItem = styled.div`
  display: grid;
  grid-gap: 5px;
`;

const LinkUnderline = styled(Typography)`
  opacity: 1;
  cursor: pointer;
  border-bottom: 1px solid ${({ theme }) => theme.colors.primary};
  transition: opacity
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};

  &:hover {
    opacity: 0.5;
  }

  @media ${({ theme }) => theme.device.tablet} {
    text-align: right;
  }
`;

const SongIconWrapper = styled(Flex)`
  align-items: center;
  justify-content: center;
  padding: 24px;
  border-radius: 10px;
  aspect-ratio: 1 /1;
  background-color: ${({ theme }) => theme.colors.secondary};
  width: 327px;
  height: 327px;

  svg {
    path {
      fill: ${({ theme }) => theme.colors.primary};
    }
  }

  @media ${({ theme }) => theme.device.tablet} {
    width: 298px;
    height: 298px;
  }
`;
